import CircleImageBadge from "assets/svgs/circle-image.svg";
import { classNames } from "utils/helper/helper";
import { Button, Spinner } from "@nextui-org/react";
import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { ImCancelCircle } from "react-icons/im";
import { BiCheckCircle, BiCrop } from "react-icons/bi";
import { getEnvProps } from "utils/helper/server-helper";
import { alertMessage } from "../toolkit/initial-state.component";
import { getHeaders } from "utils/header";
import { logger } from "lib/logger";
import { useDispatch } from "react-redux";
import { galleryActions } from "store/gallery/gallery.slice";
import ImageEditorModal from "./ImageEditorModal";
import BatchEditModal from "./BatchEditModal";
import type { FileWithStatus, ValidationResult } from "./uploader.types";

interface UploadFileProps {
  multiple?: boolean;
  extension?: string;
  canvasSize?: {
    width: number;
    height: number;
  };
  maxSize?: string;
}

const UploadFile: React.FC<UploadFileProps> = ({
  multiple = false,
  extension,
  canvasSize,
  maxSize,
}) => {
  const [files, setFiles] = useState<FileWithStatus[]>([]);
  const [error, setError] = useState(false);
  const [editingFile, setEditingFile] = useState<FileWithStatus | null>(null);
  const [showBatchEditModal, setShowBatchEditModal] = useState(false);
  const dispatch = useDispatch();

  const parseSize = (size: string): number => {
    const units = size.slice(-2).toLowerCase();
    const value = parseInt(size.slice(0, -2), 10);
    if (units === "kb") {
      return value * 1024;
    } else if (units === "mb") {
      return value * 1024 * 1024;
    } else {
      throw new Error("Invalid size format. Use 'kb' or 'mb'.");
    }
  };

  const maxSizeBytes = maxSize ? parseSize(maxSize) : undefined;

  // Generate unique ID for files
  const generateFileId = (file: File): string => {
    return `${file.name}-${file.size}-${file.lastModified}-${Date.now()}`;
  };

  const validateFile = (file: File): Promise<ValidationResult> => {
    return new Promise((resolve) => {
      // Check extension first
      if (
        extension &&
        !file.name.toLowerCase().endsWith(extension.toLowerCase())
      ) {
        resolve({
          isValid: false,
          needsAdjustment: false,
          error: `File extension must be ${extension}`,
        });
        return;
      }

      // Check file size only when provided
      if (maxSizeBytes != null && file.size > maxSizeBytes) {
        resolve({
          isValid: false,
          needsAdjustment: false,
          error: `File size (${(file.size / (1024 * 1024)).toFixed(
            2
          )}MB) exceeds maximum allowed size of ${maxSize}`,
        });
        return;
      }

      // For non-image files, we can't check dimensions
      if (!file.type.startsWith("image/")) {
        resolve({
          isValid: true,
          needsAdjustment: false,
        });
        return;
      }

      // If no canvasSize specified, don't require adjustment
      if (!canvasSize) {
        resolve({
          isValid: true,
          needsAdjustment: false,
          requiresAdjustment: false,
        });
        return;
      }

      // For images, allow adjustment in the editor when size doesn't match
      const img = new Image();
      img.src = URL.createObjectURL(file);

      img.onload = () => {
        const { width, height } = img;
        const needsAdjustment =
          width !== canvasSize.width || height !== canvasSize.height;

        resolve({
          isValid: true,
          needsAdjustment,
          requiresAdjustment: needsAdjustment,
          actualDimensions: { width, height },
        });

        URL.revokeObjectURL(img.src);
      };

      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        resolve({
          isValid: false,
          needsAdjustment: false,
          error: "Unable to process image file",
        });
      };
    });
  };

  // Start editing for a specific file
  const startEditing = (fileWithStatus: FileWithStatus) => {
    setEditingFile(fileWithStatus);

    setFiles((prev) =>
      prev.map((f) =>
        f.id === fileWithStatus.id
          ? { ...f, status: "editing", isBeingEdited: true }
          : f
      )
    );
  };

  // Cancel editing
  const cancelEditing = () => {
    if (editingFile) {
      setFiles((prev) =>
        prev.map((f) =>
          f.id === editingFile.id
            ? {
                ...f,
                status:
                  f.status === "requires_editing"
                    ? "requires_editing"
                    : "ready_to_edit",
                isBeingEdited: false,
              }
            : f
        )
      );
    }

    setEditingFile(null);
  };

  // Save edited image and upload
  const handleEditorSave = async (exportedBlob: Blob) => {
    if (!editingFile) return;

    const editedFile = new File([exportedBlob], editingFile.file.name, {
      type: "image/jpeg",
      lastModified: Date.now(),
    });

    const updatedFile: FileWithStatus = {
      ...editingFile,
      file: editedFile,
      status: "uploading",
      loading: true,
      isBeingEdited: false,
      needsAdjustment: false,
      editingCompleted: true,
    };

    setFiles((prev) =>
      prev.map((f) => (f.id === editingFile.id ? updatedFile : f))
    );

    setEditingFile(null);

    uploadFile(updatedFile);
  };

  // Batch edit all files that need editing
  const startBatchEditing = () => {
    const filesToEdit = files.filter(
      (f) =>
        (f.needsAdjustment && f.status === "ready_to_edit") ||
        f.status === "requires_editing"
    );
    if (filesToEdit.length > 0) {
      startEditing(filesToEdit[0]);
      setShowBatchEditModal(false);
    }
  };

  // Remove file from the list
  const removeFile = (fileId: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const uploadFile = async (fileWithStatus: FileWithStatus) => {
    const formData = new FormData();
    formData.append("file", fileWithStatus.file);

    const envProps = await getEnvProps();
    const hostURL = envProps.base_url;
    const headers = await getHeaders(undefined, true);

    try {
      const response = await fetch(`${hostURL}/api/v1/file/upload`, {
        method: "POST",
        body: formData,
        headers,
      });

      if (!response.ok) {
        throw new Error("Failed to upload");
      }

      const data = await response.json();
      console.log("File uploaded successfully:", data);

      if (data.data) dispatch(galleryActions.setGallery(data.data));

      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileWithStatus.id
            ? { ...f, loading: false, success: true, status: "success" }
            : f
        )
      );
    } catch (error) {
      let errorMessage = "An unknown error occurred.";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      console.error("Error uploading file:", errorMessage);

      setFiles((prev) =>
        prev.map((f) =>
          f.id === fileWithStatus.id
            ? {
                ...f,
                uploadError: errorMessage,
                loading: false,
                status: "error",
              }
            : f
        )
      );
    }
  };

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      setError(false);

      const processFiles = async (files: File[]) => {
        const newFiles: FileWithStatus[] = [];
        let filesToEdit: FileWithStatus[] = [];

        for (const file of files) {
          const validation = await validateFile(file);
          const fileId = generateFileId(file);

          if (!validation.isValid) {
            newFiles.push({
              id: fileId,
              file,
              originalFile: file,
              uploadError: validation.error,
              loading: false,
              status: "error",
              needsAdjustment: false,
            });
          } else if (validation.requiresAdjustment) {
            const newFile: FileWithStatus = {
              id: fileId,
              file,
              originalFile: file,
              loading: false,
              status: "requires_editing",
              needsAdjustment: true,
            };
            newFiles.push(newFile);
            filesToEdit.push(newFile);
          } else {
            // Files that already match canvas size or no canvas constraint can be uploaded directly
            const newFile: FileWithStatus = {
              id: fileId,
              file,
              originalFile: file,
              loading: true,
              status: "uploading",
              needsAdjustment: false,
            };
            newFiles.push(newFile);
            uploadFile(newFile);
          }
        }

        setFiles((prev) => [...prev, ...newFiles]);

        // Show batch edit modal if there are files that need editing
        if (filesToEdit.length > 0) {
          setTimeout(() => setShowBatchEditModal(true), 500);
        }
      };

      processFiles(acceptedFiles);
    },
    [extension, canvasSize, maxSize]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles: multiple ? 10 : 1,
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".gif", ".bmp", ".webp"],
    },
    onDropRejected(fileRejections) {
      const errorMessage = fileRejections[0].errors[0].message;
      setError(true);
      logger.error("errorMessage: ", errorMessage);
      alertMessage("error", errorMessage);
    },
  });

  // Get counts for batch modal
  const filesToEditCount = files.filter(
    (f) =>
      f.status === "requires_editing" ||
      (f.needsAdjustment && f.status === "ready_to_edit")
  ).length;
  const successCount = files.filter((f) => f.status === "success").length;
  const errorCount = files.filter((f) => f.status === "error").length;

  const placeholderText = isDragActive
    ? "Drop the files here..."
    : canvasSize
    ? `Drag 'n' drop some files here, or click to select files (Canvas: ${canvasSize.width}×${canvasSize.height}px)`
    : "Drag 'n' drop some files here, or click to select files";

  return (
    <div className="h-[60vh]">
      <ImageEditorModal
        isOpen={!!editingFile}
        fileStatus={editingFile}
        onCancel={cancelEditing}
        onSave={handleEditorSave}
        canvasSize={canvasSize}
      />

      <BatchEditModal
        isOpen={showBatchEditModal}
        filesToEditCount={filesToEditCount}
        canvasSize={canvasSize}
        onCancel={() => setShowBatchEditModal(false)}
        onStartEditing={startBatchEditing}
      />

      <div
        {...getRootProps()}
        className={classNames(
          "px-[12px] py-[24px] relative cursor-pointer h-full",
          "bg-neutralgraygray-25 dark:bg-dark-base/40 rounded-lg overflow-hidden border border-dashed",
          error ? "border-red-500" : "border-colourinputfield"
        )}
      >
        <input {...getInputProps()} />

        {files.length === 0 && (
          <div className="flex flex-col items-center justify-center mt-[20vh] gap-5">
            <CircleImageBadge />
            <p className="font-regular text-neutralgraygray-400">
              {placeholderText}
            </p>
            {!isDragActive && <span className="upload-button">Add Image</span>}
          </div>
        )}

        {files.length > 0 && (
          <>
            {/* Summary Bar */}
            {(filesToEditCount > 0 || successCount > 0 || errorCount > 0) && (
              <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-between">
                <div className="flex items-center gap-4 text-sm">
                  {successCount > 0 && (
                    <span className="text-green-600">
                      ✓ {successCount} uploaded
                    </span>
                  )}
                  {filesToEditCount > 0 && (
                    <span className="text-blue-600">
                      ✂ {filesToEditCount} need adjustment
                    </span>
                  )}
                  {errorCount > 0 && (
                    <span className="text-red-600">✗ {errorCount} failed</span>
                  )}
                </div>
                {filesToEditCount > 0 && (
                  <Button
                    size="sm"
                    color="primary"
                    onClick={() => setShowBatchEditModal(true)}
                  >
                    Edit Images
                  </Button>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {files.map((fileStatus) => (
                <div
                  key={fileStatus.id}
                  className="relative group rounded-lg overflow-hidden h-fit border border-gray-200 dark:border-gray-600"
                >
                  <img
                    src={URL.createObjectURL(fileStatus.file)}
                    alt={fileStatus.file.name}
                    className="w-full h-32 object-cover"
                  />

                  {/* Status Overlays */}
                  {fileStatus.status === "uploading" && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/75 dark:bg-black/75 backdrop-blur-sm">
                      <div className="text-center">
                        <Spinner size="lg" />
                        <p className="text-sm mt-2">Uploading...</p>
                      </div>
                    </div>
                  )}

                  {(fileStatus.status === "ready_to_edit" ||
                    fileStatus.status === "requires_editing") && (
                    <div className="absolute inset-0 flex items-center justify-center bg-blue-500/75 backdrop-blur-sm">
                      <div className="text-center text-white">
                        <BiCrop className="mx-auto h-8 w-8 mb-2" />
                        <p className="text-sm mb-2">
                          {fileStatus.status === "requires_editing"
                            ? "Needs Adjustment"
                            : "Ready to Edit"}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            color="primary"
                            variant="solid"
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditing(fileStatus);
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            color="danger"
                            variant="solid"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeFile(fileStatus.id);
                            }}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}

                  {fileStatus.status === "error" && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-500/75 backdrop-blur-sm">
                      <ImCancelCircle className="text-white h-8 w-8 mb-2" />
                      <p className="text-white text-xs text-center px-2">
                        {fileStatus.uploadError}
                      </p>
                      <Button
                        size="sm"
                        color="danger"
                        variant="solid"
                        className="mt-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(fileStatus.id);
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  )}

                  {fileStatus.status === "success" && (
                    <div className="absolute inset-0 flex items-center justify-center bg-green-500/75 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity">
                      <BiCheckCircle className="text-white h-8 w-8" />
                    </div>
                  )}

                  {/* Manual edit button for successful uploads */}
                  {fileStatus.status === "success" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditing(fileStatus);
                      }}
                      className="absolute bottom-2 right-2 bg-blue-500 hover:bg-blue-600 text-white p-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Re-edit image"
                    >
                      <BiCrop className="h-4 w-4" />
                    </button>
                  )}

                  {/* File name */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate">
                    {fileStatus.file.name}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default UploadFile;
